import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
  type ReactNode,
} from 'react'
import { useQueryClient, type QueryClient } from '@tanstack/react-query'

import { apiRequest, setJwt } from '../api/client'
import type {
  AcceptInviteResponse,
  ApiError,
  AuthResponse,
  Membership,
  Role,
  TodayResponse,
} from '../api/types'
import { getStartParam, getWebApp, tgStorage } from '../telegram/tg'
import { prefetchDirectorLanding, prefetchManagerLanding } from '../boot/prefetchLanding'
import { MOCK, mockAuth } from '../dev/mock'
import { IS_DEV } from '../env'

/** The moment auth resolves to a single active tenant, warm that role's landing
 *  screen + (manager) its data, in parallel with React committing the authed
 *  tree. Removes the post-auth chunk+data round-trip from first paint.
 *  Director dashboards force-refetch on mount by design, so we only warm the
 *  chunk for them — not the data. */
function warmLanding(
  qc: QueryClient,
  memberships: Membership[],
  activeTenantId: number | null,
): void {
  const role = memberships.find((m) => m.tenant_id === activeTenantId)?.role
  if (role === 'director') {
    prefetchDirectorLanding()
    return
  }
  prefetchManagerLanding()
  void qc
    .prefetchQuery({
      queryKey: ['today'],
      queryFn: () => apiRequest<TodayResponse>('/api/miniapp/today'),
      staleTime: 30_000,
    })
    .catch(() => {})
}

export type AuthStatus =
  | 'idle'             // booting, no decision yet
  | 'authing'          // POST /auth (or /accept-invite) in flight
  | 'ok'               // got JWT
  | 'orphan'           // valid Telegram user, no membership anywhere
  | 'picker'           // got JWT but multiple memberships — user must pick
  | 'invite_expired'   // M-4: invite token past exp
  | 'invite_invalid'   // M-4: invite token bad / tampered / bad initData
  | 'already_connected'// M-4: invite already consumed but user is bound
  | 'error'            // bad_hash | expired | invalid | server | …

export interface AuthState {
  status: AuthStatus
  user: AuthResponse['user'] | null
  memberships: Membership[]
  activeTenantId: number | null
  errorReason: string | null
  /** M-4: true when this session was just created via accept-invite for the
   *  first time — drives the one-time Welcome + tutorial onboarding. */
  firstRun: boolean
}

interface AuthContextValue extends AuthState {
  selectTenant: (tenantId: number) => Promise<void>
  retry: () => void
  /** M-4: leave the AlreadyConnected screen → into the app (or re-auth). */
  continueAfterInvite: () => void
}

const AuthContext = createContext<AuthContextValue | null>(null)

const INIT: AuthState = {
  status: 'idle',
  user: null,
  memberships: [],
  activeTenantId: null,
  errorReason: null,
  firstRun: false,
}

interface ProviderProps {
  children: ReactNode
  /** Override initData for local dev (?mock_auth=1). */
  mockInitData?: string
}

export function AuthProvider({ children, mockInitData }: ProviderProps) {
  const [state, setState] = useState<AuthState>(INIT)
  const [retryNonce, setRetryNonce] = useState(0)
  const qc = useQueryClient()
  // Invite acceptance is one-shot: once attempted, retry()/autopoll fall back
  // to normal /auth so a spent or bad token can't trap the boot in a loop.
  const inviteTriedRef = useRef(false)

  const auth = useCallback(async () => {
    // Dev mock (?mock=1): skip the network and load fixtures so the UI renders
    // in a plain browser. Dead in production (MOCK === false).
    if (MOCK) {
      // ?mock_invite=first|connected|expired|invalid previews the M-4 flow.
      // Synchronous + idempotent, so (unlike the real path) it needs no
      // one-shot guard — that guard would let StrictMode's double-invoke fall
      // through to the plain mock and clobber firstRun.
      const mi = new URLSearchParams(window.location.search).get('mock_invite')
      if (mi) {
        if (mi === 'expired') return setState({ ...INIT, status: 'invite_expired' })
        if (mi === 'invalid') return setState({ ...INIT, status: 'invite_invalid' })
        setJwt(mockAuth.jwt)
        return setState({
          status: mi === 'connected' ? 'already_connected' : 'ok',
          user: mockAuth.user, memberships: mockAuth.memberships,
          activeTenantId: mockAuth.active_tenant_id, errorReason: null,
          firstRun: mi === 'first',
        })
      }
      // ?mock=orphan previews the unbound-user flow (orphan + request access).
      if (new URLSearchParams(window.location.search).get('mock') === 'orphan') {
        return setState({ ...INIT, status: 'orphan', errorReason: 'orphan' })
      }
      setJwt(mockAuth.jwt)
      setState({
        status: mockAuth.memberships.length > 1 ? 'picker' : 'ok',
        user: mockAuth.user,
        memberships: mockAuth.memberships,
        activeTenantId: mockAuth.active_tenant_id,
        errorReason: null,
        firstRun: false,
      })
      return
    }

    const wa = getWebApp()
    const initData = mockInitData ?? wa?.initData ?? ''

    // ── M-4: invite deeplink (one-shot) ──────────────────────────────────
    if (!inviteTriedRef.current) {
      const sp = getStartParam()
      const m = sp ? /^invite_(.+)$/.exec(sp) : null
      if (m) {
        inviteTriedRef.current = true
        await acceptInvite(m[1], initData)
        return
      }
    }

    if (!initData) {
      setState({ ...INIT, status: 'error', errorReason: 'no_init_data' })
      return
    }

    setState((s) => ({ ...s, status: 'authing', errorReason: null }))
    try {
      const resp = await apiRequest<AuthResponse>('/api/miniapp/auth', {
        method: 'POST',
        body: { initData },
        unauthenticated: true,
      })
      setJwt(resp.jwt)
      // Best-effort persist — failure is non-fatal, JWT lives in memory
      // for the session and we re-auth on next launch.
      void tgStorage.set('jwt', resp.jwt).catch(() => {})
      void tgStorage.set(
        'active_tenant',
        String(resp.active_tenant_id),
      ).catch(() => {})

      const picker = resp.memberships.length > 1
      // Warm the landing screen + data the instant we know the role, racing the
      // React commit below (skip when a picker stands between auth and a screen).
      if (!picker) warmLanding(qc, resp.memberships, resp.active_tenant_id)
      setState({
        status: picker ? 'picker' : 'ok',
        user: resp.user,
        memberships: resp.memberships,
        activeTenantId: resp.active_tenant_id,
        errorReason: null,
        firstRun: false,
      })
    } catch (e) {
      const err = e as ApiError
      if (err.reason === 'orphan') {
        setState({ ...INIT, status: 'orphan', errorReason: 'orphan' })
        return
      }
      setState({
        ...INIT,
        status: 'error',
        errorReason: err.reason || 'server',
      })
    }
  }, [mockInitData, qc])

  // M-4: trade an invite token for a session. Maps the backend reasons to the
  // onboarding screens; `used` (token already spent) falls back to normal auth
  // since the user may already be bound from their first acceptance.
  const acceptInvite = useCallback(async (token: string, initData: string) => {
    if (!initData) {
      setState({ ...INIT, status: 'invite_invalid' })
      return
    }
    setState((s) => ({ ...s, status: 'authing', errorReason: null }))
    try {
      const resp = await apiRequest<AcceptInviteResponse>('/api/miniapp/accept-invite', {
        method: 'POST',
        body: { initData, token },
        unauthenticated: true,
      })
      setJwt(resp.jwt)
      void tgStorage.set('jwt', resp.jwt).catch(() => {})
      void tgStorage.set('active_tenant', String(resp.active_tenant_id)).catch(() => {})
      setState({
        status: resp.is_first_time ? 'ok' : 'already_connected',
        user: resp.user,
        memberships: resp.memberships,
        activeTenantId: resp.active_tenant_id,
        errorReason: null,
        firstRun: resp.is_first_time,
      })
    } catch (e) {
      const err = e as ApiError
      if (err.reason === 'expired') setState({ ...INIT, status: 'invite_expired' })
      else if (err.reason === 'used') setState({ ...INIT, status: 'already_connected' })
      else setState({ ...INIT, status: 'invite_invalid' })
    }
  }, [])

  useEffect(() => {
    void auth()
  }, [auth, retryNonce])

  const selectTenant = useCallback(async (tenantId: number) => {
    // M-1: switching active tenant rebuilds the JWT via re-auth so the
    // server picks the new active_tenant_id. Simplest correct option —
    // a dedicated /switch endpoint can come in M-2 if perf matters.
    setState((s) => ({ ...s, activeTenantId: tenantId }))
    // Persist user choice — sticky across launches.
    void tgStorage.set('active_tenant', String(tenantId)).catch(() => {})
    setState((s) => ({ ...s, status: 'ok' }))
  }, [])

  const retry = useCallback(() => setRetryNonce((n) => n + 1), [])

  // AlreadyConnected → app. With a session in hand, go straight in; otherwise
  // (the `used`-without-binding edge) re-run normal auth — invite is one-shot
  // now, so this resolves to ok/orphan rather than looping.
  const continueAfterInvite = useCallback(() => {
    setState((s) => {
      if (s.memberships.length > 0) return { ...s, status: 'ok', firstRun: false }
      return s
    })
    setRetryNonce((n) => (state.memberships.length > 0 ? n : n + 1))
  }, [state.memberships.length])

  return (
    <AuthContext.Provider value={{ ...state, selectTenant, retry, continueAfterInvite }}>
      {children}
    </AuthContext.Provider>
  )
}

export function useAuth(): AuthContextValue {
  const ctx = useContext(AuthContext)
  if (!ctx) throw new Error('useAuth must be used inside <AuthProvider>')
  return ctx
}

/** The user's role in their active tenant (drives manager vs director shell).
 *  In dev, `?mock_role=director|manager` overrides so the director UI can be
 *  built without a real director JWT (docs/miniapp-m3-tz.md §6.1). */
export function useActiveRole(): Role | null {
  const { memberships, activeTenantId } = useAuth()
  if (IS_DEV && typeof window !== 'undefined') {
    const m = new URLSearchParams(window.location.search).get('mock_role')
    if (m === 'director' || m === 'manager') return m
  }
  const active = memberships.find((x) => x.tenant_id === activeTenantId)
  return (active?.role as Role) ?? null
}
